import React, { useState } from "react";
import { 
  MapPin, 
  Phone, 
  Mail, 
  MessageCircle, 
  Clock, 
  Send,
  Building2,
  CheckCircle
} from "lucide-react";
import { toast } from "sonner";
import { useSiteSettings } from "@/context/SiteSettingsContext";

export default function ContactUs() {
  const { settings } = useSiteSettings();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name || !phone || !message) {
      toast.error("Please provide your name, phone, and message.");
      return;
    }
    setSent(true);
    toast.success("Thank you! Your message has been sent.");
  };

  const handleDirectWhatsApp = () => {
    const p = settings.whatsapp_number.replace(/[^0-9]/g, "");
    window.open(`https://wa.me/${p}?text=Hello%20Anwer%20Fancy%20Lace,%20I%20have%20an%20inquiry.`, "_blank");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-12">
      
      {/* Title */}
      <div className="text-center max-w-2xl mx-auto space-y-2">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#996515]">
          Customer Support & Wholesale Desk
        </span>
        <h1 
          data-testid="contact-page-title"
          className="font-heading text-3xl sm:text-4xl font-bold text-stone-900"
        >
          Contact Anwer Fancy Lace
        </h1>
        <p className="text-xs sm:text-sm text-stone-500">
          Have a question about complete set yardage, custom bulk orders, or tracking an existing parcel? Get in touch with us.
        </p>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        
        {/* Contact Info (5 cols) */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-[#1C1917] text-white p-6 sm:p-8 rounded-3xl border-2 border-[#D4AF37] space-y-6 shadow-xl">
            <h3 className="font-heading font-bold text-xl text-white">
              Get in Touch Directly
            </h3>

            <div className="space-y-4 text-xs">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/20 text-[#D4AF37] flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4" />
                </div>
                <div>
                  <strong className="text-white block mb-0.5">Physical Outlet / Warehouse:</strong>
                  <span className="text-stone-300">{settings.address}</span>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/20 text-[#D4AF37] flex items-center justify-center shrink-0">
                  <Phone className="w-4 h-4" />
                </div>
                <div>
                  <strong className="text-white block mb-0.5">Direct Phone & WhatsApp:</strong>
                  <span className="text-stone-300">{settings.whatsapp_number}</span>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/20 text-[#D4AF37] flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4" />
                </div>
                <div>
                  <strong className="text-white block mb-0.5">Official Inquiries:</strong>
                  <span className="text-stone-300">{settings.email}</span>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/20 text-[#D4AF37] flex items-center justify-center shrink-0">
                  <Clock className="w-4 h-4" />
                </div>
                <div>
                  <strong className="text-white block mb-0.5">Market Hours:</strong>
                  <span className="text-stone-300">Monday - Saturday: 10:30 AM to 9:30 PM (PST)</span>
                </div>
              </div>
            </div>

            <div className="pt-2">
              <button
                type="button"
                onClick={handleDirectWhatsApp}
                data-testid="contact-whatsapp-btn"
                className="w-full py-3 px-4 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold rounded-xl flex items-center justify-center gap-2 shadow-md transition"
              >
                <MessageCircle className="w-4 h-4" />
                <span>Chat on WhatsApp Now</span>
              </button>
            </div>
          </div>
        </div>

        {/* Message Form (7 cols) */}
        <div className="lg:col-span-7">
          <div className="bg-white p-6 sm:p-8 rounded-3xl border border-[#E5E0D8] shadow-luxury space-y-6">
            <h3 className="font-heading font-bold text-xl text-stone-900">
              Send an Online Inquiry
            </h3>

            {sent ? (
              <div className="bg-emerald-50 border border-emerald-300 rounded-2xl p-8 text-center space-y-3">
                <CheckCircle className="w-10 h-10 text-emerald-700 mx-auto" />
                <h4 className="font-heading font-bold text-stone-900">Message Received</h4>
                <p className="text-xs text-stone-600">
                  We will contact you via WhatsApp or phone shortly.
                </p>
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  className="px-4 py-2 bg-[#1C1917] text-white text-xs font-bold rounded-lg"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 text-xs">
                <div className="space-y-1">
                  <label className="font-semibold text-stone-800">
                    Your Full Name <span className="text-rose-600">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    data-testid="contact-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Fatima Tariq"
                    className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="font-semibold text-stone-800">
                      Mobile / WhatsApp <span className="text-rose-600">*</span>
                    </label>
                    <input
                      type="tel"
                      required
                      data-testid="contact-phone-input"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="0300 1234567"
                      className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="font-semibold text-stone-800">
                      Email Address (Optional)
                    </label>
                    <input
                      type="email"
                      data-testid="contact-email-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@example.com"
                      className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="font-semibold text-stone-800">
                    Your Message / Product Inquiry <span className="text-rose-600">*</span>
                  </label>
                  <textarea
                    required
                    rows={4}
                    data-testid="contact-message-input"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Tell us what lace designs or assistance you need..."
                    className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  className="w-full py-3 px-4 bg-[#1C1917] hover:bg-[#D4AF37] text-white hover:text-[#1C1917] text-xs font-bold rounded-xl shadow-md transition flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  <span>Send Message</span>
                </button>
              </form>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
